import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    DefaultButton,
    TextField,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    MessageBar,
    MessageBarType
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom'; // For app-login callback redirect
import { Spinner } from '@fluentui/react';

function Settings({ userInfo }) {
    const navigate = useNavigate();

    // State management
    const [accounts, setAccounts] = useState([]);
    const [newAccountEmail, setNewAccountEmail] = useState('');
    const [newAuthFlow, setNewAuthFlow] = useState('app-login');
    const [isResolvingTenant, setIsResolvingTenant] = useState(false);
    const [message, setMessage] = useState(null);
    const [isDeviceLogin, setIsDeviceLogin] = useState(false);
    const [deviceLoginInfo, setDeviceLoginInfo] = useState(null);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [newCalendarType, setNewCalendarType] = useState('upstream');
    const [newMailboxId, setNewMailboxId] = useState('');
    const [newUserIdInTenant, setNewUserIdInTenant] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [calendars, setCalendars] = useState({ upstream: [], downstream: [] });
    const [syncInterval, setSyncInterval] = useState(60);
    const [syncSettings, setSyncSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    //console.log('userInfo:', userInfo);

    // Fetch accounts, calendars, sync settings once authenticated
    useEffect(() => {
        if (userInfo) {
            fetchAccounts();
            fetchCalendars();
            fetchSyncSettings();
        }
    }, [userInfo]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        if (code && state && userInfo) {
            handleAppCallback();
        }
    }, [userInfo]);

    const fetchAccounts = async () => {
        if (!userInfo?.userId) return;

        setIsLoading(true);
        try {
            const response = await fetch('/api/accounts-list', {
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-client-principal-id': userInfo.userId
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            const data = text ? JSON.parse(text) : [];
            setAccounts(data);

        } catch (error) {
            setMessage({
                text: `Error fetching accounts: ${error.message}`,
                type: MessageBarType.error
            });
            setAccounts([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Convert a user-friendly work/school email address into the underlying
    // Azure AD tenant GUID via /api/resolve-tenant. Returns null and surfaces a
    // MessageBar on failure so the caller can simply abort.
    const resolveTenantForEmail = async (email) => {
        setIsResolvingTenant(true);
        try {
            const response = await fetch(
                `/api/resolve-tenant?email=${encodeURIComponent(email)}`,
                { headers: { 'x-ms-client-principal-id': userInfo.userId } }
            );
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || `Tenant lookup failed (HTTP ${response.status})`);
            }
            return data.tenantId;
        } catch (error) {
            setMessage({
                text: `Could not find an Azure AD tenant for "${email}": ${error.message}`,
                type: MessageBarType.error
            });
            return null;
        } finally {
            setIsResolvingTenant(false);
        }
    };

    const addAccount = async () => {
        console.log('Adding account with userInfo:', userInfo);
        if (!userInfo) {
            setMessage({
                text: 'Please sign in to continue',
                type: MessageBarType.error
            });
            return;
        }

        setMessage(null);

        const email = (newAccountEmail || '').trim();
        if (!email) {
            setMessage({ text: 'Enter a work or school email address.', type: MessageBarType.error });
            return;
        }

        const tenantId = await resolveTenantForEmail(email);
        if (!tenantId) return;

        if (newAuthFlow === 'app-login') {
            try {
                const response = await fetch('/api/accounts-create', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-ms-client-principal-id': userInfo.userId
                    },
                    body: JSON.stringify({
                        tenantId,
                        authType: 'app-login',
                        loginHint: email,
                        prompt: 'login',
                        select_account: true
                    })
                });


                // Log the response for debugging
                console.log('Response status:', response.status);
                const responseText = await response.text();
                const data = responseText ? JSON.parse(responseText) : {};


                if (data.authUrl) {
                    window.location.href = data.authUrl;
                }


            } catch (error) {
                console.log('Error details:', error);
                setMessage({
                    text: 'Error starting app-login: ' + error.message,
                    type: MessageBarType.error
                });
            }
        } else
            if (newAuthFlow === 'device-login') {
                try {
                    console.log('Starting device login flow for tenant:', tenantId);
                    console.log('Starting device login with userInfo:', userInfo);

                    // Start device auth flow without waiting
                    fetch('/api/accounts-create', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-ms-client-principal-id': userInfo.userId
                        },
                        body: JSON.stringify({
                            tenantId,
                            authType: 'device-login'
                        })
                    }).then(() => console.log('Account creation process initiated'));

                    console.log('Fetching device code info...');
                    const response = await fetch(`/api/device-auth-info?tenantId=${tenantId}`, {
                        headers: {
                            'x-ms-client-principal-id': userInfo.userId
                        }
                    });

                    if (response.ok) {
                        const deviceCodeInfo = await response.json();
                        console.log('Received device code info:', deviceCodeInfo);
                        setIsDeviceLogin(true);
                        setDeviceLoginInfo(deviceCodeInfo);

                        console.log('Starting completion check polling...');
                        const checkInterval = setInterval(async () => {
                            console.log('Polling device auth status...');
                            const statusResponse = await fetch(`/api/device-auth-info?tenantId=${tenantId}`, {
                                headers: {
                                    'x-ms-client-principal-id': userInfo.userId
                                }
                            });

                            if (statusResponse.ok) {
                                const data = await statusResponse.json();
                                console.log('Poll response:', data);
                                setDeviceLoginInfo(data);  // Update the display with latest info

                                if (data.status === 'completed') {
                                    console.log('Auth completed, cleaning up...');
                                    clearInterval(checkInterval);
                                    console.log('Refreshing accounts list...');
                                    fetchAccounts();
                                    setIsDeviceLogin(false);
                                }
                            }
                        }, 5000);

                    }
                } catch (error) {
                    console.error('Failed to get device code info:', error);
                    setMessage({
                        text: `Error during device login: ${error.message}`,
                        type: MessageBarType.error
                    });
                }
            }



    };

    // Callback for app-login (use in your React Router)
    const handleAppCallback = async () => {
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        console.log('Callback received:', { code, state });

        if (code && state) {
            try {
                // Clear URL parameters to prevent reuse and to keep the
                // single-use `code` out of browser history.
                window.history.replaceState({}, document.title, '/settings');

                // The PKCE codeVerifier and tenantId live server-side keyed
                // by `state` — we just forward state + code. See security
                // audit H-1.
                const response = await fetch('/api/accounts-create', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-ms-client-principal-id': userInfo.userId
                    },
                    body: JSON.stringify({
                        authType: 'app-login',
                        code,
                        state
                    })
                });
                console.log('Token exchange response:', await response.clone().text());

                if (response.ok) {
                    setMessage({
                        text: 'Account added successfully!',
                        type: MessageBarType.success
                    });
                    await fetchAccounts(); // Refresh the accounts list
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to create account');
                }
            } catch (error) {
                setMessage({
                    text: `Error adding account: ${error.message}`,
                    type: MessageBarType.error
                });
                console.error('Account creation error:', error);
            }
        }
    };
    const fetchCalendars = async () => {
        const response = await fetch('/api/calendars-list', {
            headers: {
                'x-ms-client-principal-id': userInfo.userId
            }
        });
        const data = await response.json();
        setCalendars(data);
    };

    const lookupCalendars = async () => {
        if (!selectedAccountId) return;
        const tenantId = accounts.find(a => a.id === selectedAccountId)?.tenantId;
        if (!tenantId) return;

        setIsLookingUp(true);
        setAvailableCalendars([]);
        setNewMailboxId('');
        try {
            const params = new URLSearchParams({ tenantId });
            if (newEmail) params.append('email', newEmail);
            const response = await fetch(`/api/calendars-lookup?${params}`, {
                headers: { 'x-ms-client-principal-id': userInfo.userId }
            });
            if (!response.ok) throw new Error(`Lookup failed: ${response.status}`);
            const data = await response.json();
            setAvailableCalendars(data);
            if (newEmail) setNewUserIdInTenant(newEmail);
            else setNewUserIdInTenant('');
        } catch (error) {
            setMessage({ text: `Error looking up calendars: ${error.message}`, type: MessageBarType.error });
        } finally {
            setIsLookingUp(false);
        }
    };

    const addCalendar = async () => {
        try {
            const account = accounts.find(a => a.id === selectedAccountId);
            const selectedCalendar = availableCalendars.find(c => c.id === newMailboxId);
            // Prefer the owner Graph actually reported for this calendar over
            // whatever the user typed in the "Calendar owner email" search box
            // — those should match for shared lookups but Graph is canonical.
            // Fall back to the authenticated account's identity for the user's
            // own calendars.
            const ownerEmail = selectedCalendar?.ownerEmail
                || newEmail
                || account?.userEmail
                || null;
            const ownerDisplayName = selectedCalendar?.ownerName
                || (newEmail ? null : account?.userDisplayName)
                || null;

            // Bidirectional = two Cosmos rows (one upstream, one downstream).
            // We issue two parallel POSTs against the single-type endpoint
            // rather than introducing a transactional shape on the API.
            const directions = newCalendarType === 'both'
                ? ['upstream', 'downstream']
                : [newCalendarType];

            const postOne = (direction) => fetch('/api/calendars-create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-client-principal-id': userInfo.userId
                },
                body: JSON.stringify({
                    tenantId: account?.tenantId,
                    mailboxId: newMailboxId,
                    type: direction,
                    userIdInTenant: newUserIdInTenant || null,
                    calendarName: selectedCalendar?.name || null,
                    ownerEmail,
                    ownerDisplayName
                })
            }).then(async (response) => {
                if (!response.ok) {
                    const body = await response.text().catch(() => '');
                    throw new Error(body || `HTTP ${response.status}`);
                }
            });

            const results = await Promise.allSettled(directions.map(postOne));
            const failures = results.filter(r => r.status === 'rejected');
            if (failures.length > 0) {
                throw new Error(failures.map(f => f.reason?.message || String(f.reason)).join('; '));
            }

            setMessage({
                text: directions.length > 1
                    ? 'Calendar added in both directions'
                    : 'Calendar added successfully',
                type: MessageBarType.success
            });
            fetchCalendars();
            setNewMailboxId('');
            setNewUserIdInTenant('');
            setNewEmail('');
            setAvailableCalendars([]);
        } catch (error) {
            setMessage({
                text: `Error adding calendar: ${error.message}`,
                type: MessageBarType.error
            });
        }
    };

    const removeAccount = async (account) => {
        if (!account?.id) return;
        const label = account.userEmail || account.tenantName || account.tenantId;
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(
            `Remove tenant "${label}"?\n\nThis deletes the stored token and any configured calendars for this tenant. You will need to re-authorise to use it again.`
        )) return;

        try {
            const response = await fetch('/api/accounts-delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-client-principal-id': userInfo.userId
                },
                body: JSON.stringify({
                    accountId: account.id,
                    tenantId: account.tenantId
                })
            });
            if (!response.ok) {
                const body = await response.text().catch(() => '');
                throw new Error(body || `HTTP ${response.status}`);
            }
            setMessage({ text: `Removed tenant "${label}".`, type: MessageBarType.success });
            await Promise.all([fetchAccounts(), fetchCalendars()]);
            if (selectedAccountId === account.id) setSelectedAccountId('');
        } catch (error) {
            setMessage({
                text: `Failed to remove tenant: ${error.message}`,
                type: MessageBarType.error
            });
        }
    };



    // Two configured-calendar rows form a "Both" pair when they share the
    // same (tenantId, mailboxId) but point in opposite directions.
    const findPairMate = (item) => {
        const all = [...calendars.upstream, ...calendars.downstream];
        return all.find(c =>
            c.id !== item.id &&
            c.tenantId === item.tenantId &&
            c.mailboxId === item.mailboxId &&
            c.type !== item.type
        ) || null;
    };

    const deleteCalendarRow = async (calendarId) => {
        const response = await fetch('/api/calendars-delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-ms-client-principal-id': userInfo.userId
            },
            body: JSON.stringify({ calendarId })
        });
        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(body || `HTTP ${response.status}`);
        }
    };

    const removeCalendar = async (item) => {
        try {
            const mate = findPairMate(item);
            let idsToDelete = [item.id];
            if (mate) {
                // eslint-disable-next-line no-restricted-globals, no-alert
                const both = window.confirm(
                    `This calendar is configured as Bidirectional.\n\n` +
                    `OK  = remove BOTH directions (upstream + downstream).\n` +
                    `Cancel = remove only the "${item.type}" row and keep the "${mate.type}" row.`
                );
                if (both) idsToDelete = [item.id, mate.id];
            }

            const results = await Promise.allSettled(idsToDelete.map(deleteCalendarRow));
            const failures = results.filter(r => r.status === 'rejected');
            if (failures.length > 0) {
                throw new Error(failures.map(f => f.reason?.message || String(f.reason)).join('; '));
            }

            setMessage({
                text: idsToDelete.length > 1
                    ? 'Bidirectional calendar removed (both directions)'
                    : 'Calendar removed successfully',
                type: MessageBarType.success
            });
            fetchCalendars();
        } catch (error) {
            setMessage({
                text: `Error removing calendar: ${error.message}`,
                type: MessageBarType.error
            });
        }
    };

    const fetchSyncSettings = async () => {
        if (userInfo) {
            const response = await fetch('/api/sync-status', {
                headers: {
                    'x-ms-client-principal-id': userInfo.userId
                }
            });
            const data = await response.json();
            setSyncSettings(data.settings);
            setSyncInterval(data.settings?.refreshIntervalMinutes || 60);
        }
    };

    const saveSyncSettings = async () => {
        try {
            await fetch('/api/sync-configure', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-client-principal-id': userInfo.userId
                },
                body: JSON.stringify({
                    refreshIntervalMinutes: parseInt(syncInterval)
                })
            });

            setMessage({
                text: 'Sync settings saved successfully',
                type: MessageBarType.success
            });
            fetchSyncSettings();
        } catch (error) {
            setMessage({
                text: `Error saving sync settings: ${error.message}`,
                type: MessageBarType.error
            });
        }
    }

    const reauthorizeAccount = async (account) => {
        console.log('Reauthorizing tenant:', account.tenantId);

        try {
            const response = await fetch('/api/accounts-create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-client-principal-id': userInfo.userId
                },
                body: JSON.stringify({
                    tenantId: account.tenantId,
                    authType: 'app-login',
                    // Pre-fill the email on the MS login page when we have it
                    // from a previous Graph enrichment; falls back to blank.
                    loginHint: account.userEmail || '',
                    prompt: 'login',
                    select_account: true
                })
            });

            const data = await response.json();
            if (data.authUrl) {
                window.location.href = data.authUrl;
            }
        } catch (error) {
            setMessage({
                text: 'Error starting reauthorization: ' + error.message,
                type: MessageBarType.error
            });
        }
    };


    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {message && (
                <MessageBar messageBarType={message.type}>
                    {message.text}
                </MessageBar>
            )}
            {isLoading ? (
                <Spinner label="Loading settings..." />
            ) : (
                <Stack>
                    <Stack.Item>
                        <Text variant="xLarge">Account Management</Text>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <TextField
                                label="Work or school email"
                                placeholder="you@yourcompany.com"
                                value={newAccountEmail}
                                onChange={(e, value) => setNewAccountEmail(value)}
                                description="We use this to find your Azure AD tenant — you don't need to know the tenant ID."
                            />
                            <Stack>
                                <Text>Authentication Flow</Text>
                                <select
                                    value={newAuthFlow}
                                    onChange={(e) => setNewAuthFlow(e.target.value)}
                                    style={{ padding: '8px', marginTop: '5px' }}
                                >
                                    <option value="app-login">Application Login</option>
                                    <option value="device-login">Device Login</option>
                                </select>
                            </Stack>

                            <DefaultButton
                                text={isResolvingTenant ? 'Finding tenant…' : 'Start Registration'}
                                onClick={addAccount}
                                disabled={!newAccountEmail || !newAuthFlow || isResolvingTenant}
                            />
                            {/* Show device-login instructions */}
                            {isDeviceLogin && deviceLoginInfo && (
                                <Stack tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 20 } }}>
                                    <Text variant="large" style={{ fontWeight: 'bold' }}>Device Login Instructions</Text>
                                    <Text>Your code: <span style={{ fontWeight: 'bold', fontSize: '24px' }}>{deviceLoginInfo.userCode}</span></Text>
                                    <Text>
                                        Visit <a href={deviceLoginInfo.verificationUri} target="_blank" rel="noopener noreferrer">
                                            {deviceLoginInfo.verificationUri}
                                        </a> and enter this code to authenticate
                                    </Text>
                                    <Text>This window will automatically update once authentication is complete</Text>
                                </Stack>
                            )}
                            <DetailsList
                                items={accounts}
                                layoutMode={DetailsListLayoutMode.justified}
                                selectionMode={SelectionMode.none}
                                columns={[
                                    {
                                        key: 'tenant',
                                        name: 'Tenant',
                                        minWidth: 140,
                                        maxWidth: 280,
                                        isResizable: true,
                                        onRender: (item) => (
                                            <Stack>
                                                <Text>{item.tenantName || item.tenantId}</Text>
                                                {item.tenantName && (
                                                    <Text variant="small" styles={{ root: { color: '#605e5c', fontFamily: 'Consolas, monospace' } }}>
                                                        {item.tenantId}
                                                    </Text>
                                                )}
                                            </Stack>
                                        )
                                    },
                                    {
                                        key: 'user',
                                        name: 'Account',
                                        minWidth: 160,
                                        maxWidth: 320,
                                        isResizable: true,
                                        onRender: (item) => (
                                            <Stack>
                                                <Text>{item.userDisplayName || item.userEmail || '—'}</Text>
                                                {item.userDisplayName && item.userEmail && (
                                                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                                        {item.userEmail}
                                                    </Text>
                                                )}
                                            </Stack>
                                        )
                                    },
                                    {
                                        key: 'authType',
                                        name: 'Auth',
                                        fieldName: 'authType',
                                        minWidth: 80,
                                        maxWidth: 120,
                                        isResizable: true
                                    },
                                    {
                                        key: 'actions',
                                        name: 'Actions',
                                        minWidth: 200,
                                        isResizable: true,
                                        onRender: (item) => (
                                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                                <DefaultButton
                                                    text="Reauthorize"
                                                    onClick={() => reauthorizeAccount(item)}
                                                />
                                                <DefaultButton
                                                    text="Remove"
                                                    onClick={() => removeAccount(item)}
                                                    styles={{ root: { backgroundColor: '#d13438', color: 'white', borderColor: '#d13438' } }}
                                                />
                                            </Stack>
                                        )
                                    }
                                ]}
                            />

                        </Stack>
                    </Stack.Item>
                    {/* Calendar Configuration Section */}
                    <Stack.Item>
                        <Text variant="xLarge">Calendar Configuration</Text>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <select
                                value={selectedAccountId}
                                onChange={(e) => { setSelectedAccountId(e.target.value); setAvailableCalendars([]); setNewMailboxId(''); setNewEmail(''); setNewUserIdInTenant(''); }}
                                style={{ padding: '8px', marginTop: '5px' }}
                                disabled={accounts.length === 0}
                            >
                                <option value="">Select an account</option>
                                {accounts.map(account => {
                                    const label = account.userEmail
                                        ? `${account.userEmail}${account.tenantName ? ` (${account.tenantName})` : ''}`
                                        : (account.tenantName || account.tenantId);
                                    return (
                                        <option key={account.id} value={account.id}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>

                            <select
                                value={newCalendarType}
                                onChange={(e) => setNewCalendarType(e.target.value)}
                                style={{ padding: '8px', marginTop: '5px' }}
                            >
                                <option value="upstream">Upstream Calendar</option>
                                <option value="downstream">Downstream Calendar</option>
                                <option value="both">Bidirectional (both)</option>
                            </select>

                            <TextField
                                label="Calendar owner email (optional)"
                                value={newEmail}
                                onChange={(e, value) => setNewEmail(value)}
                                placeholder="someone@yourcompany.com"
                                description="Leave blank to list your own calendars. Enter another mailbox's email address to add a shared calendar — the account above must already have permission to access that mailbox in O365."
                            />

                            <DefaultButton
                                text={isLookingUp ? "Looking up..." : "Lookup Calendars"}
                                onClick={lookupCalendars}
                                disabled={!selectedAccountId || isLookingUp}
                            />

                            {availableCalendars.length > 0 && (
                                <Stack tokens={{ childrenGap: 4 }}>
                                    <select
                                        value={newMailboxId}
                                        onChange={(e) => setNewMailboxId(e.target.value)}
                                        style={{ padding: '8px', marginTop: '5px' }}
                                    >
                                        <option value="">Select a calendar</option>
                                        {availableCalendars.map(cal => {
                                            // Several mailboxes have multiple calendars
                                            // with the same display name ("Calendar",
                                            // "Birthdays"). Disambiguate with owner
                                            // email and a (default) marker for the
                                            // user's primary calendar.
                                            const parts = [cal.name || '(unnamed)'];
                                            if (cal.ownerEmail) parts.push(`— ${cal.ownerEmail}`);
                                            if (cal.isDefaultCalendar) parts.push('(default)');
                                            return (
                                                <option key={cal.id} value={cal.id}>
                                                    {parts.join(' ')}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    {newMailboxId && (() => {
                                        const sel = availableCalendars.find(c => c.id === newMailboxId);
                                        if (!sel) return null;
                                        return (
                                            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                                Owner: {sel.ownerName || '(unknown)'}
                                                {sel.ownerEmail ? ` <${sel.ownerEmail}>` : ''}
                                                {sel.isDefaultCalendar ? ' • default calendar' : ''}
                                                {' • '}
                                                <span style={{ fontFamily: 'Consolas, monospace' }}>id: {sel.id.slice(0, 24)}…</span>
                                            </Text>
                                        );
                                    })()}
                                </Stack>
                            )}

                            <DefaultButton
                                text="Add Calendar"
                                onClick={addCalendar}
                                disabled={!selectedAccountId || !newMailboxId}
                            />

                            <Text variant="large">Configured Calendars</Text>
                            <DetailsList
                                items={[...calendars.upstream, ...calendars.downstream]}
                                layoutMode={DetailsListLayoutMode.justified}
                                selectionMode={SelectionMode.none}
                                columns={[
                                    {
                                        key: 'calendar',
                                        name: 'Calendar',
                                        minWidth: 160,
                                        maxWidth: 320,
                                        isResizable: true,
                                        onRender: (item) => {
                                            const account = accounts.find(a => a.tenantId === item.tenantId);
                                            return (
                                                <Stack>
                                                    <Text>{item.calendarName || '(unnamed calendar)'}</Text>
                                                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                                        {item.ownerEmail
                                                            || (item.userIdInTenant && item.userIdInTenant !== account?.userId ? item.userIdInTenant : null)
                                                            || account?.userEmail
                                                            || '(owner unknown)'}
                                                    </Text>
                                                </Stack>
                                            );
                                        }
                                    },
                                    {
                                        key: 'tenant',
                                        name: 'Tenant',
                                        minWidth: 140,
                                        maxWidth: 240,
                                        isResizable: true,
                                        onRender: (item) => {
                                            const account = accounts.find(a => a.tenantId === item.tenantId);
                                            return (
                                                <Stack>
                                                    <Text>{account?.tenantName || item.tenantId}</Text>
                                                    {account?.tenantName && (
                                                        <Text variant="small" styles={{ root: { color: '#605e5c', fontFamily: 'Consolas, monospace' } }}>
                                                            {item.tenantId}
                                                        </Text>
                                                    )}
                                                </Stack>
                                            );
                                        }
                                    },
                                    {
                                        key: 'type',
                                        name: 'Direction',
                                        minWidth: 100,
                                        maxWidth: 140,
                                        isResizable: true,
                                        onRender: (item) => {
                                            const paired = !!findPairMate(item);
                                            if (paired) {
                                                return (
                                                    <Text styles={{ root: { fontWeight: 600 } }}>
                                                        ↕ Both
                                                    </Text>
                                                );
                                            }
                                            return <Text>{item.type}</Text>;
                                        }
                                    },
                                    {
                                        key: 'actions',
                                        name: 'Actions',
                                        minWidth: 100,
                                        isResizable: true,
                                        onRender: (item) => (
                                            <DefaultButton
                                                text="Remove"
                                                onClick={() => removeCalendar(item)}
                                                styles={{ root: { backgroundColor: '#d13438', color: 'white', borderColor: '#d13438' } }}
                                            />
                                        )
                                    }
                                ]}
                            />
                        </Stack>
                    </Stack.Item>

                    {/* Sync Settings Section */}
                    <Stack.Item>
                        <Text variant="xLarge">Sync Settings</Text>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <TextField
                                label="Sync Interval (minutes)"
                                type="number"
                                min={1}
                                value={syncInterval.toString()}
                                onChange={(e, value) => setSyncInterval(value)}
                            />
                            <DefaultButton
                                text="Save Sync Settings"
                                onClick={saveSyncSettings}
                                disabled={!syncInterval || syncInterval < 1}
                            />
                        </Stack>

                    </Stack.Item>
                </Stack>
            )}
        </Stack>
    )
}

export default Settings;